import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../../../firebaseConfig";
import { setRequests } from "../../../../store/commonSlice";

/**
 * Custom hook to fetch and manage shipping request orders data
 * @param {Object} selectedFilters - Current filter state
 * @returns {boolean} Loading state
 */
export const useShippingRequestData = (selectedFilters) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.value);
  const [loading, setLoading] = useState(false);

  // Cache for user data to avoid repeated fetches
  const getUserData = async (userId) => {
    if (!getUserData.cache) {
      getUserData.cache = new Map();
    }

    if (getUserData.cache.has(userId)) {
      return getUserData.cache.get(userId);
    } else {
      const userDocRef = doc(db, "users", userId);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.exists() ? userDocSnap.data() : null;
      getUserData.cache.set(userId, userData);
      return userData;
    }
  };

  // Cache for suite data to avoid repeated fetches
  const getSuiteData = async (suiteId) => {
    if (!getSuiteData.cache) {
      getSuiteData.cache = new Map();
    }

    if (getSuiteData.cache.has(suiteId)) {
      return getSuiteData.cache.get(suiteId);
    } else {
      const suiteDocRef = doc(db, "mySuites", suiteId);
      const suiteDocSnap = await getDoc(suiteDocRef);
      const suiteData = suiteDocSnap.exists() ? suiteDocSnap.data() : null;
      getSuiteData.cache.set(suiteId, suiteData);
      return suiteData;
    }
  };

  useEffect(() => {
    if (!user?.role) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const orderConditions = [];

        // Build filter conditions
        if (selectedFilters?.routePartnerId) {
          orderConditions.push(
            where(
              "warehouseRoutePartnerId",
              "==",
              selectedFilters?.routePartnerId
            )
          );
        }
        if (selectedFilters?.warehouseId) {
          orderConditions.push(
            where("warehouseAddressId", "==", selectedFilters?.warehouseId)
          );
        }

        const querySnapshot = collection(db, "prepareShipmentRequests");
        const q = query(
          querySnapshot,
          ...orderConditions,
          orderBy("createdAt", "desc")
        );
        const snapshot = await getDocs(q);

        const promises = snapshot.docs.map(async (docSnapshot) => {
          const orderData = docSnapshot.data();
          const userId = orderData.userId;
          const mySuiteId = orderData.mySuiteId;
          const packageIds = orderData.packageIds || [];
          console.log(packageIds, "packageIds");

          let isConsolidated = false;

          // Check packages
          if (packageIds.length > 0) {
            const packageChecks = await Promise.all(
              packageIds.map(async (packageId) => {
                const packageDoc = await getDoc(
                  doc(db, "shelfPackages", packageId)
                );
                const packageData = packageDoc.data();

                // Filter by route partner if user is Route Partner Admin
                if (
                  user?.role === "Route Partner Admin" &&
                  user?.routePartnerId !== packageData?.warehouseRoutePartnerId
                ) {
                  return false;
                }

                if (packageData?.isPackageConsolidated) {
                  isConsolidated = true;
                }

                // Exclude packages with SHIPMENT_CREATED status
                if (packageData?.packageStatus === "SHIPMENT_CREATED") {
                  return false;
                }

                return true;
              })
            );
            console.log(packageChecks, "packageChecks");

            if (packageChecks.some((check) => check === false)) {
              return null;
            }
          }

          // Exclude consolidated packages
          if (isConsolidated) return null;

          // Fetch suite data if mySuiteId exists
          let uniqueAddressCode = null;
          if (mySuiteId) {
            const suiteData = await getSuiteData(mySuiteId);
            if (suiteData) {
              uniqueAddressCode = suiteData.uniqueAddressCode || null;
            }
          }

          // Fetch user data if userId exists
          if (userId) {
            console.log(orderData, "orderData");
            const userData = await getUserData(userId);

            if (userData) {
              return {
                ...orderData,
                id: docSnapshot.id,
                name: userData.name,
                profileImage: userData.profileImage,
                phoneVerified: userData.phoneVerified,
                country: userData.country,
                bayNumber: uniqueAddressCode, // Set uniqueAddressCode as bayNumber for compatibility
                uniqueAddressCode: uniqueAddressCode,
              };
            } else {
              return {
                ...orderData,
                id: docSnapshot.id,
                bayNumber: uniqueAddressCode,
                uniqueAddressCode: uniqueAddressCode,
              };
            }
          } else {
            return {
              ...orderData,
              id: docSnapshot.id,
              bayNumber: uniqueAddressCode,
              uniqueAddressCode: uniqueAddressCode,
            };
          }
        });

        const arr = await Promise.all(promises);
        // Filter out null and undefined elements
        const filteredArr = arr.filter(
          (item) => item !== null && item !== undefined
        );
        console.log(filteredArr, "arr in booking");
        dispatch(setRequests(filteredArr));
      } catch (error) {
        console.error("Error fetching shipping requests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.role, user?.routePartnerId, selectedFilters, dispatch]);

  return loading;
};

